import type { JSX } from "react";
import { useCallback, useState } from "react";

import { ContactForm } from "./ContactForm";

import { useDialogModal } from "@/lib/use-dialog-modal";

interface ContactCtaProps {
  slug: string;
  cityName: string;
  instagramHandle?: string | null;
}

/**
 * Section 06's call to action: one button, and the form behind it.
 *
 * The form used to sit inline, which stretched the guide's last screen and made
 * the one thing to do the smallest element on it. The button is the form's own
 * submit button, promoted; the dialog is the site's usual modal — a native
 * `<dialog>`, so the backdrop, Escape and focus come from the platform.
 */
export function ContactCta({ slug, cityName, instagramHandle }: ContactCtaProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const dialogRef = useDialogModal(open, close);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="atlas-guide-contact-cta bg-accent px-6 py-3 font-anton text-lg uppercase tracking-wide text-ink transition-opacity disabled:opacity-40"
      >
        SEND A MESSAGE
      </button>

      {/* The form only ever exists behind a click, so a reader without scripting
          loses the inline form's own fallback note: this is that note, and it is
          the only part of the section that works with JavaScript off. */}
      <noscript className="font-mono text-[0.65rem] tracking-wider text-paper-muted">
        {instagramHandle
          ? `This form needs JavaScript. Message us on Instagram at @${instagramHandle} instead.`
          : "This form needs JavaScript. Turn it on to send us a message."}
      </noscript>

      {/* Rendered only while open: the form unmounts on close, so no status, error
          or spent captcha token survives into the next open. */}
      {open && (
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- Native <dialog> handles Escape and is an interactive element
        <dialog
          ref={dialogRef}
          aria-modal="true"
          aria-label={`Contact the ${cityName} guide team`}
          onClick={(event) => {
            // The dialog element covers the viewport, so a click that lands on it
            // rather than on the panel inside is the backdrop.
            if (event.target === event.currentTarget) close();
          }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-transparent p-4 backdrop:bg-ink/85 backdrop:backdrop-blur-sm"
        >
          <div className="relative w-full max-w-lg border border-white/15 bg-ink p-8">
            <h2 className="mb-6 font-anton text-3xl uppercase leading-none text-paper">
              SEND A MESSAGE
            </h2>
            <ContactForm slug={slug} cityName={cityName} />
            {/* Last in the DOM on purpose: `<dialog>` focuses its first focusable
                descendant, and that should be the name field, not this button. */}
            <button
              type="button"
              onClick={close}
              aria-label="Close dialog"
              className="absolute right-4 top-4 font-mono text-sm text-paper-muted transition-colors hover:text-accent"
            >
              ✕
            </button>
          </div>
        </dialog>
      )}
    </>
  );
}
